/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { stringify } from 'qs'
import { PropsWithChildren, useCallback, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router'
import PageTitle from '../../../../client/js/components/PageTitle'
import Pagination from '../../../../client/js/components/pagination/Pagination'
import PaginationProvider from '../../../../client/js/components/pagination/PaginationProvider'
import useQueryParams from '../../../../client/js/hooks/useQueryParams'
import { JOB_STATUS_PENDING } from '../../enums/JobStatusCodes.mjs'
import {
  fetchManagementReportJobs,
  fetchManagementReports,
  fetchManagementReportYearsAvailable,
  generateManagementReports
} from '../../lib/rest/managementReportAPIClient'
import CustomerInput from '../inputs/CustomerInput'
import EstateInput from '../inputs/EstateInput'
import ManagementReportList from './ManagementReportList'
import { useMutation, useQuery } from '@tanstack/react-query'
import usePaginationQuery from '../../../../client/js/components/pagination/usePaginationQuery'
import { getHalEmbedded, getHalPageTotalElements } from '@jalik/hal'
import LoadingHelper from '../../../../client/js/components/LoadingHelper'
import useParsedQueryParam from '../../../../client/js/hooks/useParsedQueryParam'
import { parseNumber } from '@jalik/form-parser'
import {
  getManagementReportGenerateLink,
  getManagementReportListLink,
  getManagementReportViewLink
} from '../../lib/links/managementReportLinks'
import { Alert, Box, Button, Card, Container, Group, Select, Stack, Text } from '@mantine/core'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import { DateTime } from 'luxon'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons/faExclamationTriangle'
import Page from '../../../../client/js/components/Page'
import BreadcrumbCard from '../../../../client/js/components/breadcrumb/BreadcrumbCard'
import AdminBreadcrumb from '../../../../client/js/components/breadcrumb/AdminBreadcrumb'

export function ManagementReportGeneratorPageLink (props: PropsWithChildren) {
  const { children, ...others } = props
  return (
    <AnchorLink {...others} to={getManagementReportGenerateLink()}>
      {children}
    </AnchorLink>
  )
}

const initialQuery = {
  page: 1,
  limit: 10
}

function ManagementReportGeneratorPage () {
  const intl = useIntl()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useQueryParams()

  const customerId = useParsedQueryParam('customerId', parseNumber)
  const estateId = useParsedQueryParam('estateId', parseNumber)
  const year = useParsedQueryParam('year', parseNumber)

  // Filters

  const customerFilter = useMemo(() => ({
    isLessor: true
  }), [])

  const estateFilter = useMemo(() => ({
    customerId,
    transaction: 'L'
  }), [customerId])

  // Loaders

  const jobsQuery = useQuery({
    enabled: true,
    queryKey: ['managementReportsJobs'],
    queryFn: () => fetchManagementReportJobs({ status: JOB_STATUS_PENDING })
  })
  const jobIds = useMemo(() => (
    (jobsQuery.data?.data != null ? jobsQuery.data.data : null) || []
  ), [jobsQuery.data?.data])

  const [totalElements, setTotalElements] = useState<number>(0)
  const pagination = usePaginationQuery(totalElements)
  const { limit, page } = pagination

  const reportsQuery = useQuery({
    enabled: searchParams.jobId != null,
    queryKey: ['managementReports', searchParams, page, limit],
    queryFn: () => fetchManagementReports({
      ...initialQuery,
      jobId: searchParams.jobId,
      limit,
      page
    }).then((r) => {
      setTotalElements(getHalPageTotalElements(r))
      return r
    }),
    refetchInterval: 3000
  })
  const reports = useMemo(() => (
    (reportsQuery.data != null ? getHalEmbedded(reportsQuery.data)?.managementReports : null) || []
  ), [reportsQuery.data])

  const yearsQuery = useQuery({
    enabled: true,
    queryKey: ['managementReportsYears', customerId, estateId],
    queryFn: () => fetchManagementReportYearsAvailable({ customerId, estateId })
  })
  const years = useMemo(() => (
    (yearsQuery.data?.data != null ? yearsQuery.data.data : null) || []
  ), [yearsQuery.data?.data])

  // Mutations

  const generateMutation = useMutation({
    mutationKey: ['generateManagementReports', customerId, estateId, year],
    mutationFn: () => generateManagementReports({ customerId, estateId, year }),
    onSuccess: (result) => {
      jobsQuery.refetch()

      if (result != null && result.success && result.jobId &&
        String(result.jobId) !== String(searchParams.jobId)) {
        navigate({
          search: stringify({
            ...searchParams,
            jobId: result.jobId
          })
        })
      }
    }
  })

  // Handlers

  const setFilter = useCallback((name: string, value?: string) => {
    setSearchParams((s) => {
      const nextParams = { ...s, [name]: value }

      if (name === 'customerId' && value == null) {
        delete nextParams.estateId
      }
      if (name === 'customerId' || name === 'estateId') {
        delete nextParams.year
      }
      return nextParams
    })
  }, [setSearchParams])

  const handleValueChange = useCallback((name: string) => (value: string) => {
    setFilter(name, value)
  }, [setFilter])

  const isCurrentYearSelected = DateTime.now().year === year

  const title = intl.formatMessage({
    id: 'management_reports_generation',
    defaultMessage: 'Génération des rapports de gérance'
  })

  return (
    <Page>
      <Container size="xl">
        <PageTitle
          hidden
          title={title}
        />

        <BreadcrumbCard>
          <AdminBreadcrumb>
            <AnchorLink to={getManagementReportListLink()}>
              <FormattedMessage
                id="management_reports"
                defaultMessage="Rapports de gérance"
              />
            </AnchorLink>
            <span>{title}</span>
          </AdminBreadcrumb>
        </BreadcrumbCard>

        <Stack>
          <Card shadow="sm">
            <Stack>
              <Box
                component="fieldset"
                disabled={generateMutation.isPending}
                p={0}
              >
                <Group align="end">
                  <CustomerInput
                    queryFilter={customerFilter}
                    name="customerId"
                    label={intl.formatMessage({
                      id: 'owner',
                      defaultMessage: 'Propriétaire'
                    })}
                    style={{ flexGrow: 1 }}
                    onChange={handleValueChange('customerId')}
                    value={customerId != null ? String(customerId) : null}
                  />
                  <EstateInput
                    queryFilter={estateFilter}
                    disabled={!customerId}
                    name="estateId"
                    style={{ flexGrow: 1 }}
                    onChange={handleValueChange('estateId')}
                    value={estateId != null ? String(estateId) : null}
                  />
                  <Select
                    disabled={yearsQuery.isFetching}
                    label={intl.formatMessage({
                      id: 'year',
                      defaultMessage: 'Année'
                    })}
                    name="year"
                    onChange={handleValueChange('year')}
                    required
                    value={year != null ? String(year) : ''}
                    data={[
                      ...years.map((value) => ({
                        value: String(value),
                        label: String(value)
                      }))
                    ]}
                  />
                  <Button
                    disabled={generateMutation.isPending || !year || isCurrentYearSelected}
                    loading={generateMutation.isPending}
                    onClick={() => {
                      generateMutation.mutate()
                    }}
                  >
                    {intl.formatMessage({
                      id: 'generate',
                      defaultMessage: 'Générer'
                    })}
                  </Button>
                </Group>
              </Box>

              {isCurrentYearSelected
                ? <Alert
                  color="yellow"
                  icon={<FontAwesomeIcon fixedWidth icon={faExclamationTriangle} />}
                >
                  <FormattedMessage
                    id="management_reports_cannot_be_generated_for_current_year"
                    defaultMessage="Les rapports de gérance ne doivent etre générés que lorsqu'ils sont complets, pour cette raison ils ne peuvent etre générés pour l'année en cours."
                  />
                  <Button
                    ms="sm"
                    component={AnchorLink}
                    to={getManagementReportViewLink({ year, customerId, estateId })}
                    color="yellow"
                  >
                    <FormattedMessage
                      id="consult_a_report"
                      defaultMessage="Consulter un rapport"
                    />
                  </Button>
                </Alert>
                : null}

              {generateMutation.isPending
                ? <Alert>
                  {intl.formatMessage({
                    id: 'management_reports_pending_text',
                    defaultMessage: 'Les rapports de gérance sont en cours de création.'
                  })}
                </Alert>
                : null}
            </Stack>
          </Card>

          <LoadingHelper
            query={jobsQuery}
            render={() => (
              <Card shadow="sm">
                {jobIds.length > 0
                  ? <Group>
                    <Text fw="bold">
                      {intl.formatMessage({
                        id: 'running_tasks',
                        defaultMessage: 'Tâches en cours'
                      })}
                    </Text>
                    <Group>
                      {(jobIds || []).map((jobId) => (
                        <Button
                          key={jobId}
                          // todo active={searchParams.jobId === String(jobId)}
                          component={AnchorLink}
                          to={`?${stringify({ jobId })}`}
                        >
                          {jobId}
                        </Button>
                      ))}
                    </Group>
                  </Group>
                  : <Alert>
                    {intl.formatMessage({
                      id: 'no_task_running',
                      defaultMessage: 'Aucune tâche en cours.'
                    })}
                  </Alert>
                }
              </Card>
            )}
          />

          {searchParams.jobId != null
            ? <LoadingHelper
              query={reportsQuery}
              render={() => (
                <PaginationProvider context={pagination}>
                  <Card shadow="sm">
                    <Stack>
                      <ManagementReportList items={reports} />
                      <Pagination />
                    </Stack>
                  </Card>
                </PaginationProvider>
              )}
            />
            : null}
        </Stack>
      </Container>
    </Page>
  )
}

export default ManagementReportGeneratorPage
